import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Link, useSearchParams } from "react-router-dom";
import CaseForm from "@/components/CaseForm";
import { formatParisDate, formatParisTime } from "@/lib/parisDateTime";

type CaseRow = {
  id: string;
  title: string;
  company_name: string | null;
  center: string | null;
  event_date: string | null;
  budget: number | null;
  status: string;
};

export default function CasesPage() {
  const [rows, setRows] = useState<CaseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const showForm = params.get("new") === "1";

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("cases")
        .select("id,title,company_name,center,event_date,budget,status")
        .limit(50)
        .order("created_at", { ascending: false });
      if (!error && data) setRows(data as unknown as CaseRow[]);
      setLoading(false);
    })();
  }, []);

  const formatEventDateTime = (eventDate: string | null): string => {
    if (!eventDate) return "";
    try {
      return `${formatParisDate(eventDate, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })} ${formatParisTime(eventDate, {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } catch {
      return eventDate;
    }
  };

  if (showForm) return <CaseForm />;

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">All Cases</h1>
        <Link className="underline" to="/cases?new=1">Create new case</Link>
      </div>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b">
                <th className="py-2">Title</th>
                <th>Company</th>
                <th>Center</th>
                <th>Event date</th>
                <th>Budget</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b hover:bg-accent/30">
                  <td className="py-2"><Link className="underline" to={`/case/${r.id}`}>{r.title}</Link></td>
                  <td>{r.company_name ?? ""}</td>
                  <td>{r.center ?? ""}</td>
                  <td>{formatEventDateTime(r.event_date)}</td>
                  <td>{r.budget ?? ""}</td>
                  <td>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
